/** Shared crawl disposition for metadata, headers and sitemap filtering. */
import { isPrivateCrawlPath, isPublicNoindexPath, legacyCrawlCanonicalPath } from "./crawl-policy";

export type Disposition =
  | { readonly kind: "index" }
  | { readonly kind: "noindex"; readonly follow: boolean }
  | { readonly kind: "redirect"; readonly target: string }
  | { readonly kind: "gone" };

function toPathname(slugOrPath: string): string {
  const trimmed = slugOrPath.replace(/^\/+|\/+$/g, "");
  if (trimmed === "") return "/";
  const last = trimmed.split("/").pop() ?? "";
  return "/" + trimmed + (last.includes(".") ? "" : "/");
}

/** Published public pages do not need traffic, prose quotas or similarity certificates. */
export function pathDisposition(slugOrPath: string): Disposition {
  const pathname = toPathname(slugOrPath);
  const redirectTarget = legacyCrawlCanonicalPath(pathname);
  if (redirectTarget) return { kind: "redirect", target: redirectTarget };
  if (isPrivateCrawlPath(pathname)) return { kind: "noindex", follow: false };
  if (isPublicNoindexPath(pathname)) return { kind: "noindex", follow: true };
  return { kind: "index" };
}

export function isIndexable(slugOrPath: string): boolean {
  return pathDisposition(slugOrPath).kind === "index";
}

export function robotsForPath(
  slugOrPath: string,
  contentAvailable = true
): {
  index: boolean;
  follow: boolean;
  googleBot?: {
    index: boolean;
    follow: boolean;
    "max-video-preview": number;
    "max-image-preview": "large" | "standard" | "none";
    "max-snippet": number;
  };
} {
  const disposition = pathDisposition(slugOrPath);
  if (disposition.kind === "index") {
    // An unavailable response must not index its error placeholder as content.
    if (!contentAvailable) return { index: false, follow: true };
    return {
      index: true,
      follow: true,
      googleBot: {
        index: true,
        follow: true,
        "max-video-preview": -1,
        "max-image-preview": "large",
        "max-snippet": -1,
      },
    };
  }
  if (disposition.kind === "noindex") {
    return { index: false, follow: disposition.follow };
  }
  return { index: false, follow: false };
}
